import { Link } from "@reach/router";
import React, { FunctionComponent, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Get } from "../../utils/api";
import { onResize } from "../../utils/navigation/overflow-menu";
import ArrowTabItem from "../shared/ArrowTabItem";
import { FullScreenIcon, LogoutIcon, PodcastIcon, SettingsIcon, SpinnerIcon, WarningIcon } from "../shared/Icon";
import LoginButton from "./LoginButton";
import LogoutButton from "./LogoutButton";
import MenuHamburger from "./MenuHamburger";
import "./NavigationNew.css";
import ClickableDiv from "../shared/ClickableDiv";
import PartyForm from "../anime-viewing/PartyForm";
import Button from "../shared/Button";
import { PartyContext } from "../../utils/party/party";
import ColorfulLoading from "../../images/ColorfulLoading";
import HentaiIcon from "../../images/Hentai";

interface IProps {
	navigationBar: NavigationBarModel | undefined;
	loggedIn: boolean | undefined;
	showTesla: boolean;
	setLoggedIn(value: IChatMetadata | boolean): void;
	loadNavigation(): void;
}

export interface NavigationBarModel {
	pages: NavigationModel[];
	userImage: number;
}

export interface NavigationModel {
	path: string;
	name: string;
	icon: string;
}

interface IUsernameResponse {
	username: string;
	chat: IChatMetadata;
}

export interface IChatMetadata {
	lastGreeting: string | null;
	lastBah: string | null;
	isHiding: boolean;
	hidingMessage: string | null;
}

const Navigation: FunctionComponent<IProps> = (props) => {
	const { navigationBar: navigationBarModel, loggedIn, showTesla, setLoggedIn, loadNavigation } = props;
	
	const [username, setUsername] = useState("");
	const [displaySide, setDisplaySide] = useState(false);
	const [showOverflow, setShowOverflow] = useState(false);

	const setLoggedInAndUser = useCallback((userData: IUsernameResponse) => {
		setUsername(userData.username);
		setLoggedIn(userData.chat);
	}, [setLoggedIn]);

	const loadUser = useCallback(() => {
		Get<IUsernameResponse>("/auth/username")
			.then(result => setLoggedInAndUser(result))
			.catch(_ex => {
				Get<IUsernameResponse>("/auth/username")
					.then(result => setLoggedInAndUser(result))
					.catch(_ex => {
						setTimeout(() => {
							Get<IUsernameResponse>("/auth/username")
								.then(result => setLoggedInAndUser(result))
								.catch(_ex => {
									setTimeout(() => {
										Get<IUsernameResponse>("/auth/username")
											.then(result => setLoggedInAndUser(result))
											.catch(_ex => {
												setLoggedIn(false);
												setUsername("");
											});
									}, 1000);
								});
						}, 100);
					});
			});
	}, [setLoggedIn, setLoggedInAndUser]);

	const loadUserAndNavigation = useCallback((loggedOut?: boolean) => {
		if (loggedOut) {
			setLoggedIn(false);
			setUsername("");
			setDisplaySide(false);
			return;
		}

		setDisplaySide(false);
		loadUser();
		loadNavigation();
	}, [loadUser, loadNavigation, setLoggedIn]);

	useEffect(() => {
		loadUserAndNavigation();
	}, [loadUserAndNavigation]);

	const toggleSideNav = useCallback(() => { setDisplaySide(!displaySide); }, [setDisplaySide, displaySide]);

	const overlayOnClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
		if (event.target === document.getElementById("myNav-overlay")) {
			toggleSideNav();
		}
	}, [toggleSideNav]);

	const closeSideNav = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
		(event.target as HTMLElement)?.blur();
		setDisplaySide(false);
	}, [setDisplaySide]);

	const closeMenus = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
		closeSideNav(event);
		setShowOverflow(false);
	}, [closeSideNav]);

	const links = useMemo(() => {
		if (!navigationBarModel) { return []; }
		return navigationBarModel.pages.map((value: NavigationModel, index: number) => {
			if (value.name === "Donate" || value.name === "ホーム") {
				return <ArrowTabItem key={value.path} groupName="leftMenu"><a href={value.path} onClick={closeMenus}>{value.name}</a></ArrowTabItem>;
			}
			return <ArrowTabItem key={value.path} groupName="leftMenu" first={index === 0}><Link to={value.path} onClick={closeMenus}>{value.name}</Link></ArrowTabItem>;
		});
	}, [closeMenus, navigationBarModel]);
	const sideBarLinks = useMemo(() => {
		if (!navigationBarModel) { return null; }
		return navigationBarModel.pages.map((value: NavigationModel, index: number) => {
			const icon = value.name !== "Hentai" 
				? <i className={`fa fa-${value.icon}`} aria-hidden="true"></i>
				: <div className="hentaiIcon"><HentaiIcon /></div>;
			if (value.name === "Donate" || value.name === "ホーム") {
				return <a key={index} href={value.path}>{icon}{value.name}</a>;
			}
			return <Link key={index} to={value.path} onClick={closeSideNav}>{icon}{value.name}</Link>;
		});
	}, [closeSideNav, navigationBarModel]);

	const { party, leaveParty } = useContext(PartyContext);

	const [isPartyFormOpen, setIsPartyFormOpen] = useState(false);
	const createParty = useCallback(async () => {
		setIsPartyFormOpen(true);
	}, []);

	const partyTile = useMemo(() => {
		if (!navigationBarModel?.pages.some(p => p.name === "Anime")) {
			return null;
		}

		if (!party.isInParty) { 
			return <ArrowTabItem groupName="rightMenu" first>
				<ClickableDiv className="party rightSideMenuButtons" onClick={createParty}><span><PodcastIcon /></span></ClickableDiv>
			</ArrowTabItem>;
		}

		const memberTiles = party.users.map(user => {
			const userImage = user.disconnected || party.disconnected
				? <ColorfulLoading />
				: <img src={`/settings/userImage/${user.id}`} alt="profile" />;
			
			return <div key={user.id} className="partyMember">
				{userImage}
				<span>{user.name}</span>
			</div>;
		});

		const icon = party.disconnected 
			? <SpinnerIcon /> 
			: party.users.some(u => u.disconnected) 
				? <WarningIcon />
				: <PodcastIcon />;

		return <ArrowTabItem groupName="rightMenu" first>
			<div className="party"><span>{icon} {party.users.length}</span><div className="partyMembersContainer">
				<div className="partyMembers">
					{memberTiles}
					<Button color="Red" size="Medium" level="Secondary" leftIcon={<LogoutIcon />} onClick={leaveParty}>Leave</Button>
				</div>
			</div></div></ArrowTabItem>;
	}, [createParty, leaveParty, navigationBarModel?.pages, party.disconnected, party.isInParty, party.users]);

	const userTile = useMemo(() => {
		const fullScreenLink = (navigator.userAgent.indexOf("Tesla") >= 0 || showTesla)
			? <ArrowTabItem groupName="rightMenu"><a href={`https://youtube.com/redirect?q=${window.location.href}`} className="rightSideMenuButtons" title="Full Screen"><FullScreenIcon /></a></ArrowTabItem>
			: null;

		if (loggedIn === undefined) {
			return <div className="topNavRight"></div>;
		}

		if (loggedIn) {
			return <div className="topNavRight">
				{partyTile}
				<ArrowTabItem groupName="rightMenu" first={!partyTile}><Link to="/Settings" className="userPlateImage"><div className="userPlateImage"><img src={`/settings/userImage/?${navigationBarModel?.userImage}`} /></div></Link></ArrowTabItem>
				<label id="userPlate">{username}</label>
				{fullScreenLink}
				<ArrowTabItem groupName="rightMenu"><Link to="/Settings" className="rightSideMenuButtons" title="Settings"><SettingsIcon /></Link></ArrowTabItem>
				<ArrowTabItem groupName="rightMenu"><LogoutButton className="rightSideMenuButtons" reload={loadUserAndNavigation}><LogoutIcon /></LogoutButton></ArrowTabItem>
			</div>;
		}
		return <div className="topNavRight">
			{fullScreenLink}
			<LoginButton reload={loadUserAndNavigation} />
		</div>;
	}, [loggedIn, loadUserAndNavigation, partyTile, navigationBarModel?.userImage, username])

	useEffect(() => {
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	// useEffect(() => {
	// 	onResize();
	// });
	useLayoutEffect(() => {
		let time: any = undefined;
		let raf1 = requestAnimationFrame(() => {
			let raf2 = requestAnimationFrame(() => {
				let raf3 = requestAnimationFrame(() => {
					onResize();
					time = setTimeout(onResize, 100);
				});
			});
		});

		
	
		return () => {
			cancelAnimationFrame(raf1);
			clearTimeout(time);
		};
	}, [navigationBarModel?.pages.length]);

	const toggleOverflow = useCallback(() => { setShowOverflow(x => !x); }, [])

	return <>
		<div className="topNavContainer">
			<nav className="topnav" id="myTopnav">
				<div className={"verticalCenter " + (displaySide && "sticky")}>
					{loggedIn && <MenuHamburger id="topNavButton" isOpen={displaySide} toggleSideNav={toggleSideNav} />}
				</div>
				<div className="topNavLeft">
					<div className="topNavLeftLinks">
						{links}
					</div>
					<div className={"topNavOverflow " + (showOverflow ? "showOverflow" : "")}>
						<ArrowTabItem  groupName="leftMenu"><button onClick={toggleOverflow}><i className="fa fa-ellipsis-h" aria-hidden="true"></i></button></ArrowTabItem>
						<div className="topNavOverflowLinks"></div>
						<div className="topNavOverflowBorderHider" />
					</div>
				</div>
				<Link to="/" className="logo" onClick={closeSideNav}>
					<span>
						<label>メ</label>
						<label>イ</label>
						<label>ド</label>
						<label>ち</label>
						<label>ゃ</label>
						<label>ん</label>
					</span>
				</Link>
				{userTile}
			</nav>
		</div>
		<div className={"nav-overlay " + (displaySide && "navOverlayOpen")} id="myNav-overlay" onClick={overlayOnClick}>
			<nav className={"sidenav " + (displaySide && "sideNavOpen")} id="mySidenav">
				<div className={"verticalCenter " + (displaySide && "sticky")}>
					<MenuHamburger id="topNavButton" isOpen={displaySide} toggleSideNav={toggleSideNav} />
				</div>
				{sideBarLinks}
				<Link to="/Settings" onClick={closeSideNav}><SettingsIcon />Settings</Link>
				<LogoutButton className="sidenavLink" reload={loadUserAndNavigation}><LogoutIcon />Logout</LogoutButton>
			</nav>
		</div>
		<PartyForm open={isPartyFormOpen} onClose={() => { setIsPartyFormOpen(false); }} />
	</>;
}

export default Navigation;